import { Injectable } from '@angular/core';
import { Product } from '../classes/product.model';
import { CartItem } from '../classes/cart-item.model';

declare global {
	interface Window {
		dataLayer: any[];
	}
}

/**
 * GA4Service — centralizza tutti i push ecommerce verso il dataLayer GTM.
 *
 * Spec di riferimento:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag
 *
 * Model refs:
 *   ProductMaster : id, name, price, sale_price, um
 *   Product       : extends ProductMaster + short_desc, category[]{name,slug}, tags[]{tag,descrizione,gruppo}
 *   CartItem      : extends ProductMaster + qty, sum
 *
 * Utilizzo:
 *   constructor(private ga4: GA4Service) {}
 *   this.ga4.viewItemList(products, params, listName);
 *   this.ga4.viewItem(product);
 *   this.ga4.addToCart(product, qty, selectedVariant?);
 *   this.ga4.viewCart(cartItems);
 *   this.ga4.beginCheckout(cartItems);
 *   this.ga4.purchase(cartItems, orderId, shipping, tax);
 *   this.ga4.signUp();
 *   this.ga4.generateLead();
 */
@Injectable({
	providedIn: 'root'
})
export class GA4Service {

	private readonly CURRENCY = 'EUR';

	// ─────────────────────────────────────────────────────────────────────────
	// PRIVATE HELPERS
	// ─────────────────────────────────────────────────────────────────────────

	/** Initialises dataLayer and clears the previous ecommerce object (GA4 best-practice). */
	private push(eventName: string, ecommerce: object): void {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ ecommerce: null });
		window.dataLayer.push({ event: eventName, ecommerce });
	}

	/** Resolves the effective price: sale_price when set, otherwise price. */
	private effectivePrice(product: Product): number {
		return product.sale_price > 0 ? product.sale_price : product.price;
	}

	/**
	 * Maps a Product to a GA4 item object.
	 * Optional fields (category levels, variant) are included only when present.
	 *
	 * @param product     The product to map.
	 * @param index       0-based position in a list (pass only for view_item_list).
	 * @param listId      item_list_id  (pass only for list events).
	 * @param listName    item_list_name (pass only for list events).
	 * @param qty         Quantity (pass for add_to_cart / cart events).
	 * @param variantLabel Human-readable variant string, e.g. "Bianco - 12V".
	 */
	private mapItem(
		product: Product,
		index?: number,
		listId?: string,
		listName?: string,
		qty?: number,
		variantLabel?: string
	): { [key: string]: any } {

		const item: { [key: string]: any } = {
			item_id:   product.id        || '',
			item_name: product.short_desc || product.name || '',
			price:     this.effectivePrice(product),
			currency:  this.CURRENCY,
		};

		if (index !== undefined)  item['index']          = index;
		if (listId)               item['item_list_id']   = listId;
		if (listName)             item['item_list_name'] = listName;
		if (qty !== undefined)    item['quantity']       = qty;
		if (variantLabel)         item['item_variant']   = variantLabel;

		// Product.category → Array<{name, slug}> — up to 3 levels
		if (product.category?.length > 0) {
			item['item_category']  = product.category[0].name;
			if (product.category[1]) item['item_category2'] = product.category[1].name;
			if (product.category[2]) item['item_category3'] = product.category[2].name;
		}

		return item;
	}

	/** Maps a CartItem (ProductMaster + qty/sum) to a GA4 item object. */
	private mapCartItem(cartItem: CartItem): { [key: string]: any } {
		const price = cartItem.sale_price > 0 ? cartItem.sale_price : cartItem.price;
		return {
			item_id:   cartItem.id   || '',
			item_name: cartItem.name || '',
			price,
			currency:  this.CURRENCY,
			quantity:  cartItem.qty,
		};
	}

	// ─────────────────────────────────────────────────────────────────────────
	// PUBLIC EVENTS
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * view_item_list — lista prodotti (categoria, ricerca, tag).
	 *
	 * @param products  Array di prodotti visibili nella lista.
	 * @param params    Query params attivi (category, tags, searchTerm).
	 * @param listName  pageTitle già risolto (es. "Alimentatori").
	 */
	viewItemList(products: Product[], params: any, listName: string): void {
		const listId = params['category']  ? `category_${params['category']}`
		             : params['tags']       ? `tag_${params['tags']}`
		             : params['searchTerm'] ? `search_${params['searchTerm']}`
		             : 'shop_list';

		const items = products.map((p, i) =>
			this.mapItem(p, i, listId, listName)
		);

		this.push('view_item_list', { item_list_id: listId, item_list_name: listName, items });
	}

	/**
	 * view_item — pagina dettaglio prodotto.
	 *
	 * @param product  Il prodotto visualizzato.
	 */
	viewItem(product: Product): void {
		const price = this.effectivePrice(product);
		const item  = this.mapItem(product);

		this.push('view_item', {
			currency: this.CURRENCY,
			value:    price,
			items:    [item],
		});
	}

	/**
	 * add_to_cart — aggiunta al carrello.
	 *
	 * @param product         Prodotto originale (per id, categoria).
	 * @param qty             Quantità aggiunta.
	 * @param selectedVariant Variante selezionata (opzionale).
	 */
	addToCart(
		product: Product,
		qty: number,
		selectedVariant?: { color: string | null; colorName: string | null; price: number | null; size: string }
	): void {
		const price = selectedVariant?.price
			? selectedVariant.price
			: this.effectivePrice(product);

		let variantLabel: string | undefined;
		if (selectedVariant?.color) {
			variantLabel = selectedVariant.colorName
				? `${selectedVariant.colorName}${selectedVariant.size ? ' - ' + selectedVariant.size : ''}`
				: selectedVariant.color;
		}

		const item = this.mapItem(product, undefined, undefined, undefined, qty, variantLabel);
		item['price'] = price; // override con prezzo variante se presente

		this.push('add_to_cart', {
			currency: this.CURRENCY,
			value:    price * qty,
			items:    [item],
		});
	}

	/**
	 * view_cart — visualizzazione carrello.
	 *
	 * @param cartItems  Array di CartItem presenti nel carrello.
	 */
	viewCart(cartItems: CartItem[]): void {
		const items = cartItems.map(ci => this.mapCartItem(ci));
		const value = cartItems.reduce((sum, ci) => sum + (ci.sum ?? ci.price * ci.qty), 0);

		this.push('view_cart', {
			currency: this.CURRENCY,
			value,
			items,
		});
	}

	/**
	 * begin_checkout — primo step del checkout.
	 *
	 * @param cartItems  Array di CartItem nel carrello al momento del checkout.
	 */
	beginCheckout(cartItems: CartItem[]): void {
		const items = cartItems.map(ci => this.mapCartItem(ci));
		const value = cartItems.reduce((sum, ci) => sum + (ci.sum ?? ci.price * ci.qty), 0);

		this.push('begin_checkout', {
			currency: this.CURRENCY,
			value,
			items,
		});
	}

	/**
	 * purchase — transazione completata.
	 *
	 * @param cartItems     Prodotti acquistati.
	 * @param orderId       ID ordine (transaction_id).
	 * @param shipping      Costo spedizione.
	 * @param tax           IVA applicata.
	 */
	purchase(cartItems: CartItem[], orderId: string, shipping: number = 0, tax: number = 0): void {
		const items = cartItems.map(ci => this.mapCartItem(ci));
		const value = cartItems.reduce((sum, ci) => sum + (ci.sum ?? ci.price * ci.qty), 0);

		this.push('purchase', {
			transaction_id: orderId,
			currency:       this.CURRENCY,
			value,
			shipping,
			tax,
			items,
		});
	}

	/**
	 * sign_up — registrazione completata con successo.
	 */
	signUp(): void {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ event: 'sign_up', method: 'email' });
	}

	/**
	 * generate_lead — invio form di contatto / richiesta informazioni.
	 *
	 * @param value    Valore stimato del lead (opzionale).
	 */
	generateLead(value?: number): void {
		window.dataLayer = window.dataLayer || [];
		const payload: { [key: string]: any } = {
			event:    'generate_lead',
			currency: this.CURRENCY,
		};
		if (value !== undefined) payload['value'] = value;
		window.dataLayer.push(payload);
	}
}
